import * as autobind from 'autobind';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Button, Container, Modal, Icon, Header } from 'semantic-ui-react';
import { Query, OrderDefinition } from 'stores/dataStore';
import { TableView, TableModel } from 'widgets/collections/table';
import { connect } from 'redux-scaffolding-ts';
import { nameof } from 'utils/object';
import { isNullOrWhiteSpaces, extractFriendlyIdNumber } from 'utils/useful-functions';
import { ClearableTimerInput } from 'widgets/editors/clearable-timer-input';
import { IwsQuestionBanksStore, IwsQuestionBankDto } from 'stores/assessments/questionBank/iws-questionBank-store';
import { LocationDto } from 'stores/configuration/locations/locations-store';
import LocationEditorWithRoleLocations from 'widgets/bussiness/location-editor-with-rolelocations';
import { IdentityService } from 'services/identity-service';
import { resolve } from 'inversify-react';

interface ChooseIWSQuestionsViewProps extends WithTranslation {
  onAddIWSQuestions: (questions: IwsQuestionBankDto[]) => void;
  onCloseModal?: () => void;
  alreadySelectedQuestions: string[];
  iwsQuestionsStore?: IwsQuestionBanksStore;
  iwsSkillId?: string;
  iwsSkillName?: string;
  category?: string;
  professionId?: string;
  fromCustomize?: boolean;
}

interface ChooseIWSQuestionsFilters {
  questionTitleOrId: string;
  location: LocationDto;
}

interface ChooseIWSQuestionsViewState {
  query: Query;
  selectedQuestions: IwsQuestionBankDto[];
  filters: ChooseIWSQuestionsFilters;
}

@connect(['iwsQuestionsStore', IwsQuestionBanksStore])
class ChooseIWSQuestionsView extends React.Component<ChooseIWSQuestionsViewProps, ChooseIWSQuestionsViewState> {
  @resolve(IdentityService)
  private identityService: IdentityService;

  private globalLocationOption = { text: 'Global', value: 'Global', id: null };

  constructor(props: ChooseIWSQuestionsViewProps) {
    super(props);

    this.state = {
      query: {
        searchQuery: ``,
        orderBy: [],
        filter: [],
        skip: 0,
        take: 10
      },
      selectedQuestions: null,
      filters: {
        questionTitleOrId: undefined,
        location: null
      }
    };
  }

  componentDidMount() {
    this.refreshTable(this.state.filters);
  }

  @autobind
  private onAddIWSQuestions() {
    this.props.onAddIWSQuestions(this.state.selectedQuestions);

    this.props.onCloseModal();
  }

  @autobind
  private onCancel() {
    this.props.onCloseModal();
  }

  @autobind
  private load() {
    this.props.iwsQuestionsStore.getAllAsync(this.state.query);
  }

  @autobind
  private handleOrderBy(orderBy: OrderDefinition[]) {
    this.setState({ query: Object.assign(this.state.query, { orderBy }) }, this.load);
  }

  @autobind
  private handlePageChange(skip: number, take: number) {
    this.setState({ query: Object.assign(this.state.query, { skip, take }) }, this.load);
  }

  @autobind
  private handleFilterChange(filters, oDatafilters) {
    let query = { ...this.state.query, filter: oDatafilters, skip: 0 };
    this.setState({ filters, query }, this.load);
  }

  private handleFilterByQuestionTitleOrId(value: string) {
    let filters = { ...this.state.filters };
    filters.questionTitleOrId = value;
    this.refreshTable(filters);
  }

  private selectedIWSQuestions(items: unknown[]): void {
    this.setState({ selectedQuestions: items as any });
  }

  private onLocationChanged = (location: LocationDto) => {
    const filters = { ...this.state.filters };
    if (!location) filters.location = null;
    else filters.location = location;

    this.setState({ filters });
    this.refreshTable(filters);
  };

  private refreshTable(filters: ChooseIWSQuestionsFilters) {
    const oDataFilters = this.buildODataFilter(filters);
    this.handleFilterChange(filters, oDataFilters);
  }

  private buildODataFilter(filters?: ChooseIWSQuestionsFilters) {
    const oDataFilters = [];
    const currentUserInfo = this.identityService.getUserInfo();
    const isPoc = IdentityService.isPoc(currentUserInfo);
    const isAdminOrPowerInst = IdentityService.isAdminOrPowerInstructor(currentUserInfo);
    const pocLocations = currentUserInfo.locationsByRoles['PoC'] as string[];
    const { category, alreadySelectedQuestions, iwsSkillId, professionId } = this.props;

    if (alreadySelectedQuestions && alreadySelectedQuestions.length > 0)
      oDataFilters.push(`not(Id in (${alreadySelectedQuestions.join(',')}))`);

    if (!isNullOrWhiteSpaces(iwsSkillId)) {
      oDataFilters.push({
        or: [{ iwsSkillId: { eq: { type: 'guid', value: iwsSkillId } } }]
      });
    }
    if (!isNullOrWhiteSpaces(category)) oDataFilters.push({ testCategory: { name: { eq: category } } });

    if (!isNullOrWhiteSpaces(professionId)) {
      oDataFilters.push({ Professions: { any: { id: { eq: { value: professionId, type: 'guid' } } } } });
    }

    if (!isNullOrWhiteSpaces(filters.questionTitleOrId)) {
      const input = filters.questionTitleOrId;
      const parts = [`contains(tolower(question/text), '${input.toLowerCase()}')`];

      const friendlyId = extractFriendlyIdNumber(input, 'QS');
      if (!Number.isNaN(friendlyId)) {
        if (input.startsWith('QS')) {
          parts.push(`cast(FriendlyId, 'Edm.String') eq '${friendlyId}'`);
        } else {
          parts.push(`contains(cast(FriendlyId, 'Edm.String'), '${friendlyId !== 0 ? friendlyId : input}')`);
        }
      }

      const f = `(${parts.join(' or ')})`;
      oDataFilters.push(f);
    }

    if (filters.location != null) {
      oDataFilters.push({ LocationId: { eq: { value: filters.location.id, type: 'guid' } } });
    } else {
      if (isPoc && pocLocations && !pocLocations.isEmpty()) {
        oDataFilters.push(`LocationId in (${pocLocations.join(',')})`);
      } else if (isAdminOrPowerInst && this.props.fromCustomize) {
        oDataFilters.push({ LocationId: { ne: { type: 'guid', value: null } } });
      }
    }

    return oDataFilters;
  }

  public render() {
    const { t } = this.props as any;
    const currentUserInfo = this.identityService.getUserInfo();
    const isPoc = IdentityService.isPoc(currentUserInfo);
    const isAdminOrPowerInst = IdentityService.isAdminOrPowerInstructor(currentUserInfo);
    const pocLocations = currentUserInfo.locationsByRoles['PoC'] as string[];

    const tableModel = {
      columns: [
        {
          title: t('Question ID'),
          tooltipRenderer: true,
          renderer: data => data.friendlyId,
          selectableHeader: true,
          sortDefinition: {
            field: nameof<IwsQuestionBankDto>('friendlyId'),
            useProfile: false
          }
        },
        {
          title: t('Questions'),
          tooltipRenderer: false,
          renderer: data => <div>{data.question.text}</div>,
          selectableHeader: true
        },
        {
          title: t('Question Source'),
          tooltipRenderer: false,
          selectableHeader: true,
          renderer: data => (
            <span key={data.id} className="question-bank__cell__tag">
              {data.type}
            </span>
          )
        }
      ],
      data: this.props.iwsQuestionsStore.state
    } as TableModel<IwsQuestionBankDto>;

    return (
      <Modal className="template-search__modal" open closeOnEscape={true} onClose={this.onCancel} closeOnDimmerClick={false}>
        <Modal.Header className="borderless-header">
          <Icon className="modal-back-icon clickable-icon" size="large" color="grey" name="arrow left" onClick={() => this.onCancel()} />
          <Header as="h2" className="modal-header-title">
            {t('Select Questions')}
          </Header>
        </Modal.Header>
        <Modal.Content className="modal-content">
          <div className="template-search__first-row__column-filters">
            <ClearableTimerInput
              icon="search"
              placeholder={t('Search in Question or ID')}
              onChange={(event, data) => this.handleFilterByQuestionTitleOrId(data.value)}
            />
            <LocationEditorWithRoleLocations
              key={'location-editor-tna-question-list-filter'}
              locationsReceived={isPoc ? pocLocations : []}
              value={null}
              onChange={location => this.onLocationChanged(location)}
              nullable
              customPlaceholder={'Question Source'}
              skipAutoSelectFirstOption
              extraLocationOptions={isAdminOrPowerInst ? [this.globalLocationOption] : []}
            />
            <span className="keep-right skill-name">
              {t('Skill')}: {this.props.iwsSkillName}
            </span>
          </div>

          <Container className="template-list-table">
            <TableView
              model={tableModel}
              selectable={true}
              onOrderByChanged={this.handleOrderBy}
              onRefresh={this.load}
              canEdit={false}
              canDelete={false}
              hiddeMenu={true}
              onSelectionChange={questions => this.selectedIWSQuestions(questions)}
              onPageChange={this.handlePageChange}
              //onFilterChange={this.handleFilterChange}
              selectionType={'checkbox'}
              unselectFirstRow
            ></TableView>
          </Container>
        </Modal.Content>
        <Modal.Actions>
          <Button className="basic" onClick={this.onCancel}>
            {t('Cancel')}
          </Button>
          <Button className="add-template-button" onClick={this.onAddIWSQuestions} positive>
            {t('Select Questions')}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

// Wire up the React component to the Redux store
export default withTranslation()(ChooseIWSQuestionsView);
